import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_3e66b0ed-93a2-4dc7-abca-ce9b0d15b317/artifacts/su7kwk53_StartEase%20Logo.png';
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const googleButtonRef = useRef(null);
  const isGoogleInitialized = useRef(false);

  // Handle Google credential response
  const handleGoogleCredentialResponse = async (response) => {
    try {
      // Send the credential to our backend
      const result = await axios.post(`${API}/auth/google/token`, {
        credential: response.credential
      });

      const { token, user } = result.data;
      
      // Login and redirect
      login(token, user);
      
      // Redirect based on user role
      if (user?.role === 'super_admin' || user?.role === 'admin') {
        navigate('/superadmin');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      console.error('Google login error:', err);
      setError(err.response?.data?.detail || 'Google login failed. Please try again.');
    }
  };

  // Initialize Google Identity Services
  useEffect(() => {
    const initializeGoogle = () => {
      if (isGoogleInitialized.current) return;
      if (!window.google || !window.google.accounts) {
        // Script not loaded yet, try again in 100ms
        setTimeout(initializeGoogle, 100);
        return;
      }

      try {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleCredentialResponse,
          auto_select: false,
          cancel_on_tap_outside: true,
        });

        if (googleButtonRef.current) {
          window.google.accounts.id.renderButton(googleButtonRef.current, {
            type: 'standard',
            theme: 'filled_black',
            size: 'large',
            text: 'continue_with',
            shape: 'rectangular',
            width: 340,
          });
        }

        isGoogleInitialized.current = true;
      } catch (err) {
        console.error('Google init error:', err);
      }
    };

    initializeGoogle();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    
    try {
      await axios.post(`${API}/auth/send-otp`, { email });
      // Navigate to OTP verification page with email
      navigate('/verify-otp', { state: { email } });
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to send code. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden" style={{ backgroundColor: 'var(--bg-base)' }}>
      {/* Subtle background pattern */}
      <div 
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, var(--accent) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }}
      />
      
      {/* Gradient overlay */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at 50% 0%, rgba(16, 185, 129, 0.08) 0%, transparent 50%)'
        }}
      />

      <div className="w-full max-w-[420px] relative z-10">
        {/* Logo & Branding */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img 
              src={LOGO_URL} 
              alt="StartEase Agent" 
              className="w-16 h-16"
            />
          </div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ color: 'var(--text-primary)' }}>StartEase Agent</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>{"US entity, tax & compliance — done for you"}</p>
        </div>

        {/* Login Card */}
        <div 
          className="rounded-lg p-10"
          style={{ 
            backgroundColor: 'var(--bg-surface)',
            border: '1px solid var(--border-subtle)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
          }}
        >
          <h2 className="text-2xl font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Welcome back</h2>
          <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>Sign in to continue to your account</p>

          {/* Google Login Button - Rendered by Google */}
          <div className="flex justify-center mb-6">
            <div 
              ref={googleButtonRef} 
              id="google-signin-button"
              data-testid="google-login-btn"
            />
          </div>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px" style={{ backgroundColor: 'var(--border-subtle)' }}></div>
            <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>or</span>
            <div className="flex-1 h-px" style={{ backgroundColor: 'var(--border-subtle)' }}></div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="mb-6">
              <label htmlFor="email" className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                Email address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full px-4 py-3.5 rounded-lg transition-all duration-200 focus:outline-none"
                style={{ 
                  backgroundColor: 'var(--bg-base)',
                  border: error ? '1px solid var(--danger-text)' : '1px solid var(--border-subtle)',
                  color: 'var(--text-primary)',
                }}
                onFocus={(e) => {
                  if (!error) e.target.style.borderColor = 'var(--accent)';
                  e.target.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.15)';
                }}
                onBlur={(e) => {
                  if (!error) e.target.style.borderColor = 'var(--border-subtle)';
                  e.target.style.boxShadow = 'none';
                }}
                disabled={loading}
                data-testid="email-input"
              />
              {error && (
                <p className="mt-2 text-sm" style={{ color: 'var(--danger-text)' }} data-testid="error-message">
                  {error}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 px-4 rounded-lg font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98]"
              style={{ backgroundColor: 'var(--accent)', color: 'var(--text-primary)' }}
              onMouseEnter={(e) => { if (!loading) e.currentTarget.style.backgroundColor = 'var(--accent-hover)'; }}
              onMouseLeave={(e) => { if (!loading) e.currentTarget.style.backgroundColor = 'var(--accent)'; }}
              data-testid="send-otp-btn"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Sending...
                </span>
              ) : (
                'Send Login Code'
              )}
            </button>
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-sm mt-8" style={{ color: 'var(--text-secondary)' }}>
          By continuing, you agree to our{' '}
          <Link to="/terms" className="hover:underline" style={{ color: 'var(--accent-soft-text)' }}>Terms</Link>
          {' '}and{' '}
          <Link to="/privacy" className="hover:underline" style={{ color: 'var(--accent-soft-text)' }}>Privacy Policy</Link>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
